import { useState, useEffect, useCallback } from "react";
import {
  listRoles,
  type RoleResponse,
} from "../api/accessManagement/rolesService";

export interface RoleOption {
  value: string;
  label: string;
}

interface UseRoleOptionsResult {
  roleOptions: RoleOption[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function mapRolesToOptions(roles: RoleResponse[]): RoleOption[] {
  return roles.map((role) => ({
    value: role.code,
    label: role.name,
  }));
}

/**
 * Fetches role options for dropdowns, with optional server-side search.
 * The search parameter is expected to be debounced by the caller.
 * The `enabled` flag allows callers to avoid triggering API calls when
 * the consumer UI (e.g. modal/drawer) is not visible.
 */
export function useRoleOptions(
  search: string = "",
  enabled: boolean = true
): UseRoleOptionsResult {
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoleOptions = useCallback(async () => {
    if (!enabled) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await listRoles({
        page: 1,
        limit: 50,
        search: search || undefined,
      });
      const options = mapRolesToOptions(data || []);
      setRoleOptions(options);
    } catch (err) {
      console.error("Failed to fetch roles for dropdown:", err);
      setError(
        err instanceof Error ? err.message : "Failed to load roles for dropdown"
      );
      setRoleOptions([]);
    } finally {
      setLoading(false);
    }
  }, [search, enabled]);

  useEffect(() => {
    if (enabled) {
      fetchRoleOptions();
    }
  }, [fetchRoleOptions, enabled]);

  const refetch = useCallback(() => {
    fetchRoleOptions();
  }, [fetchRoleOptions]);

  return { roleOptions, loading, error, refetch };
}

